import { FC } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { InfoIcon, ExternalLink } from 'lucide-react';

export const OwnSiteGuide: FC = () => {
  const steps = [
    {
      title: 'Copy the ConversionCraft snippet',
      description:
        'Log in to your ConversionCraft account and find your tracking code snippet.',
    },
    {
      title: 'Access your website files',
      description:
        'Use FTP, SSH, or your hosting control panel to access your website files.',
    },
    {
      title: 'Locate the main HTML file',
      description:
        'Find the main HTML file for your website. This is often index.html, or for PHP-based sites, it might be header.php.',
    },
    {
      title: 'Open the file for editing',
      description: 'Open the file in a text editor of your choice.',
    },
    {
      title: 'Paste the snippet',
      description:
        'Paste the ConversionCraft snippet just before the closing </head> tag.',
    },
    {
      title: 'Save changes',
      description:
        'Save the file and upload it back to your server if necessary.',
    },
  ];

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>
          How to Add ConversionCraft Snippet to Your Own Website
        </CardTitle>
        <CardDescription>
          Follow these steps to add your ConversionCraft tracking code to your
          custom website.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="list-decimal list-inside space-y-4 mb-8">
          {steps.map((step, index) => (
            <li key={index}>
              <span className="font-bold">{step.title}</span>
              <p className="ml-5 text-sm text-gray-600">{step.description}</p>
              {index === 4 && (
                <Alert className="my-4 w-full max-w-2xl">
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Tip</AlertTitle>
                  <AlertDescription className="w-full max-w-2xl overflow-x-scroll">
                    Paste your snippet just before the closing &lt;/head&gt; tag
                    in your HTML file:
                    <pre className="mt-2 bg-gray-100 p-2 rounded">
                      {`<head>\n  <!-- Other head content -->\n  <Paste your code here>\n</head>`}
                    </pre>
                  </AlertDescription>
                </Alert>
              )}
            </li>
          ))}
        </ol>
      </CardContent>
      <CardFooter>
        <a
          href="https://developer.mozilla.org/en-US/docs/Learn/HTML/Introduction_to_HTML/The_head_metadata_in_HTML"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:underline"
        >
          Learn more about HTML head section
          <ExternalLink className="ml-1 h-4 w-4" />
        </a>
      </CardFooter>
    </Card>
  );
};
